/**
 * Expediente del Cliente — buscador de entrada (/clientes).
 *
 * No hay tabla de clientes en el lado de préstamos, así que no existe "la
 * lista de clientes": se entra buscando. La búsqueda usa el mismo endpoint
 * de la paleta global (/api/buscarclientes, por nombre/cédula/chasis/número
 * de préstamo) y agrupa los resultados por documento. El endpoint devuelve
 * préstamos y también ventas al contado (filas con tipo "contado", sin
 * préstamo), así que el cliente de contado puro sí sale por nombre. Quien
 * solo tiene cotizaciones sigue sin aparecer: para ese caso el botón "Abrir
 * expediente" navega directo con el documento tecleado y el backend resuelve
 * por factmot/cotizaciones.
 *
 * "Nuevo cliente" registra a una persona sin factura (clienteregistro): sale
 * en la búsqueda con tipo "registrado" y la venta se precarga con sus datos.
 */
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import { FP, PageHeader, HeroSearch, EstadoVacio, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";
import { estadoPrestamo } from "lib/prestamos";
import { formatDocumentoAuto, tipoDocumento, documentoValido } from "lib/documento";
import RegistroClienteDialog from "components/clientes/RegistroClienteDialog";

const chipActivo = { bgcolor: "#E3F2FD", color: "#1565C0", fontWeight: 700 };
const chipNeutro = { bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary, fontWeight: 700 };

export default function Clientes() {
  const navigate = useNavigate();
  const { post } = useApi();

  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [registrando, setRegistrando] = useState(false);
  const timer = useRef(null);

  // Misma búsqueda de la paleta global, con rebote: no hace falta un enter.
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const texto = q.trim();
    if (texto.length < 3) {
      setResultados([]);
      setBuscando(false);
      return undefined;
    }
    setBuscando(true);
    timer.current = setTimeout(async () => {
      const { ok, data } = await post("/api/buscarclientes", { q: texto });
      setResultados(ok && Array.isArray(data.clientes) ? data.clientes : []);
      setBuscando(false);
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // Un documento = una tarjeta, aunque tenga varios préstamos o compras al
  // contado en los resultados (el endpoint devuelve filas, no personas).
  const clientes = useMemo(() => {
    const porCedula = new Map();
    resultados.forEach((r) => {
      const ced = String(r.cedula || "").trim();
      if (!ced) return; // sin documento no hay expediente que abrir
      // "codeudor" (tratamiento de co-deudor encendido): persona que solo
      // figura como fiador; trae cuántos préstamos garantiza.
      const codeudor = r.tipo === "codeudor";
      const contado = r.tipo === "contado";
      // "registrado": dado de alta sin factura, todavía no compra.
      const registrado = r.tipo === "registrado";
      const activo =
        !contado && !codeudor && !registrado && estadoPrestamo(r.status, r.balance) === "activo";
      const previo = porCedula.get(ced);
      if (previo) {
        if (codeudor) previo.garantizados += Number(r.garantizados) || 0;
        else if (contado) previo.contado += 1;
        else if (!registrado) previo.prestamos += 1;
        previo.activo = previo.activo || activo;
      } else {
        porCedula.set(ced, {
          cedula: ced,
          nombres: r.nombres,
          prestamos: contado || codeudor || registrado ? 0 : 1,
          contado: contado ? 1 : 0,
          garantizados: codeudor ? Number(r.garantizados) || 0 : 0,
          registrado,
          activo,
        });
      }
    });
    return [...porCedula.values()];
  }, [resultados]);

  const etiquetaCliente = (cl) => {
    if (cl.activo) return "Con préstamo activo";
    if (cl.registrado) return "Registrado sin factura";
    if (cl.prestamos === 0 && cl.contado === 0 && cl.garantizados > 0) {
      return `Co-deudor en ${cl.garantizados} ${cl.garantizados === 1 ? "préstamo" : "préstamos"}`;
    }
    if (cl.prestamos === 0 && cl.contado > 0) {
      return cl.contado > 1 ? "Compras al contado" : "Compra al contado";
    }
    return "Sin préstamo activo";
  };

  // Lo tecleado es un documento completo: se puede abrir directo aunque la
  // búsqueda (que solo ve préstamos) no lo devuelva. Se exige al menos un
  // dígito y sin espacios: si no, cualquier apellido de 6+ letras pasaría
  // por pasaporte válido.
  const texto = q.trim();
  const documentoDirecto =
    !/\s/.test(texto) && /\d/.test(texto) && documentoValido(texto, tipoDocumento(texto))
      ? texto
      : "";

  const abrir = (cedula) => navigate(`/clientes/expediente/${encodeURIComponent(cedula)}`);

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Expediente del Cliente"
        subtitle="Toda la relación de una persona con el negocio: préstamos, compras, pagos, cotizaciones y gestión"
        cta={
          <Button
            startIcon={<PersonAddAlt1OutlinedIcon />}
            onClick={() => setRegistrando(true)}
            sx={{
              textTransform: "none",
              borderRadius: 1.8,
              fontWeight: 700,
              boxShadow: "none",
              bgcolor: FP.color.navy,
              color: "#FFFFFF",
              "&:hover": { bgcolor: FP.color.navySoft, boxShadow: "none", color: "#FFFFFF" },
            }}
          >
            Nuevo cliente
          </Button>
        }
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <HeroSearch
          title="Buscar cliente"
          description="Busca por nombre, cédula/RNC, chasis o número de préstamo; incluye a quien compró al contado. También puedes escribir el documento completo para abrir el expediente directo, aunque el cliente solo tenga cotizaciones."
        >
          {/* El contenedor de children de HeroSearch es un flex row: sin
              width el Stack se encoge al contenido y el campo sale enano. */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ width: "100%" }}>
            <TextField
              fullWidth
              autoFocus
              placeholder="Nombre, documento, chasis o número de préstamo…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && documentoDirecto) abrir(documentoDirecto);
              }}
              sx={fpInputSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: FP.color.textMuted }} />
                  </InputAdornment>
                ),
              }}
            />
            {documentoDirecto && (
              <Button
                onClick={() => abrir(documentoDirecto)}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  textTransform: "none",
                  borderRadius: 1.8,
                  fontWeight: 700,
                  boxShadow: "none",
                  px: 2.5,
                  whiteSpace: "nowrap",
                  bgcolor: FP.color.navy,
                  color: "#FFFFFF",
                  "&:hover": { bgcolor: FP.color.navySoft, boxShadow: "none", color: "#FFFFFF" },
                }}
              >
                Abrir expediente
              </Button>
            )}
          </Stack>
        </HeroSearch>

        {buscando && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={28} />
          </Stack>
        )}

        {!buscando && q.trim().length >= 3 && clientes.length === 0 && !documentoDirecto && (
          <EstadoVacio
            titulo="Sin resultados"
            subtitulo="Ningún préstamo, venta al contado ni cliente registrado coincide con la búsqueda. Si el cliente solo tiene cotizaciones, escribe su documento completo para abrir el expediente directo; si es nuevo, regístralo con «Nuevo cliente»."
          />
        )}

        <Stack spacing={1.2}>
          {clientes.map((cl) => (
            <Box
              key={cl.cedula}
              onClick={() => abrir(cl.cedula)}
              sx={{
                border: `1px solid ${FP.color.border}`,
                borderRadius: 2.5,
                bgcolor: FP.color.cardBg,
                boxShadow: FP.shadow.soft,
                p: 2,
                display: "flex",
                alignItems: "center",
                gap: 1.6,
                cursor: "pointer",
                transition: "all .15s ease",
                "&:hover": { boxShadow: FP.shadow.md, borderColor: FP.color.accentBlue },
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: `${FP.color.accentBlue}12`,
                  color: FP.color.accentBlue,
                  flexShrink: 0,
                }}
              >
                <PersonOutlineIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: "0.95rem" }} noWrap>
                  {cl.nombres || "Sin nombre"}
                </Typography>
                <Typography sx={{ fontSize: "0.82rem", color: FP.color.textSecondary }}>
                  {formatDocumentoAuto(cl.cedula)}
                </Typography>
              </Box>
              <Chip
                size="small"
                label={etiquetaCliente(cl)}
                sx={cl.activo ? chipActivo : chipNeutro}
              />
              <ArrowForwardIcon sx={{ fontSize: 18, color: FP.color.textMuted }} />
            </Box>
          ))}
        </Stack>
      </Box>

      <RegistroClienteDialog
        open={registrando}
        onClose={() => setRegistrando(false)}
        onGuardado={(cedula) => {
          setRegistrando(false);
          abrir(cedula);
        }}
        onAbrirExpediente={(cedula) => {
          setRegistrando(false);
          abrir(cedula);
        }}
      />
    </Box>
  );
}
